import React, { useState, useEffect, useMemo } from "react";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import TablePagination from "../../../sharedPages/CommonComponents/NewPagination/NewPagination";
import SearchBar from "../../../sharedPages/CommonComponents/SearchBar/SearchBar";
import "./TradeListingTable.css";
import checkIcon from "../../../../assets/images/verifyMyTrade/checkNewIcon.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { setOnBoardingStartContext } from "../../../../redux/users/usersSlice";
import { useDashboardMarket } from "../../../sharedPages/CommonComponents/DashboardMarketSelect/DashboardMarketContext";

const getMarketTypeParam = (marketKey) =>
  marketKey === "spot" ? "spot" : "futures";

const formatAsset = (symbol = "") => {
  const value = String(symbol);
  if (value.includes("/")) return value;
  if (value.endsWith("USDT")) return `${value.slice(0, -4)}/USDT`;
  if (value.endsWith("USDC")) return `${value.slice(0, -4)}/USDC`;
  if (value.endsWith("FDUSD")) return `${value.slice(0, -5)}/FDUSD`;
  if (value.endsWith("USD")) return `${value.slice(0, -3)}/USD`;
  return value;
};

const formatTradeSide = (side, positionAmt) => {
  const value = String(side || "").toLowerCase();
  if (value === "buy" || value === "long") return "Long";
  if (value === "sell" || value === "short") return "Short";
  if (Number(positionAmt) > 0) return "Long";
  if (Number(positionAmt) < 0) return "Short";
  return value ? String(side) : "—";
};

/** First usable epoch ms; rejects null / 0 / invalid (avoids 01-01-1970). */
const pickTimestamp = (...values) => {
  for (const value of values) {
    if (value == null || value === "") continue;
    let n = Number(value);
    if (!Number.isFinite(n)) {
      const parsed = Date.parse(value);
      if (!Number.isFinite(parsed)) continue;
      n = parsed;
    }
    if (n <= 0) continue;
    if (n < 1e12) n *= 1000; // seconds → ms
    if (n < 946684800000) continue; // before 2000-01-01
    return n;
  }
  return null;
};

const formatDate = (value) => {
  const timestamp = pickTimestamp(value);
  if (timestamp == null) return "—";
  return dayjs(timestamp).format("DD-MM-YYYY");
};

const formatCount = (count) => Number(count || 0).toLocaleString("en-US");

const isSpotTrade = (item) => {
  if (item?.isSpot === true || item?.spot === true) return true;
  const market = String(
    item?.marketType ||
      item?.tradeType ||
      item?.accountType ||
      item?.type ||
      ""
  ).toLowerCase();
  if (market.includes("spot")) return true;
  if (market.includes("future")) return false;
  return false;
};

const OpenTradesIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M2.1 4.2C1.35 5.3 1 6.55 1 8C1 9.45 1.35 10.7 2.1 11.8"
      stroke="currentColor"
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <circle cx="8" cy="8" r="4.15" stroke="currentColor" strokeWidth="1.35" />
    <path d="M6.85 5.85L10.55 8L6.85 10.15V5.85Z" fill="currentColor" />
    <path
      d="M13.9 4.2C14.65 5.3 15 6.55 15 8C15 9.45 14.65 10.7 13.9 11.8"
      stroke="currentColor"
      strokeWidth="1.35"
      strokeLinecap="round"
    />
  </svg>
);

const ClosedTradesIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="4.2" stroke="currentColor" strokeWidth="1.35" />
    <circle cx="8" cy="8" r="1.15" fill="currentColor" />
    <path
      d="M8 1.6V3.35M8 12.65V14.4M1.6 8H3.35M12.65 8H14.4"
      stroke="currentColor"
      strokeWidth="1.35"
      strokeLinecap="round"
    />
  </svg>
);

const PendingOrdersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 2h14M5 22h14"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M17 2v4.172a2 2 0 0 1-.586 1.414L12 12l-4.414-4.414A2 2 0 0 1 7 6.172V2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 22v-4.172a2 2 0 0 1 .586-1.414L12 12l4.414 4.414A2 2 0 0 1 17 17.828V22"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const handleVerifyClick = ({
  row,
  dispatch,
  location,
  navigate,
  activeTable,
  user,
  selectedMarket,
}) => {
  if (!user?.verifiedmyTrade?.connect) {
    dispatch(setOnBoardingStartContext(location.pathname));
    navigate("/leader/verifymyTrade/tradeinfo");
    return;
  }

  if (activeTable === "closed") {
    navigate(`/leader/verifiedTrading`, {
      state: {
        fromVerifyIt: true,
        tradeType: "Closed Trades",
        tradeId: row._id,
        marketType: selectedMarket,
      },
    });
    return;
  }

  navigate(`/leader/verifiedTrading`, {
    state: {
      fromVerifyIt: true,
      tradeType: "Live Trade",
      tradeSymbol: row.assetRaw || row.asset,
      updateTime: row.date,
      marketType: selectedMarket,
    },
  });
};

const TradeListingTable = () => {
  const api = useApi();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user);
  const dispatch = useDispatch();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 8;
  const [openPosition, setOpenPosition] = useState([]);
  const [closePosition, setClosePosition] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [activeTable, setActiveTable] = useState("closed");
  const { selectedMarket, permissionType } = useDashboardMarket();
  const canShowSpotOpenTrades =
    permissionType === "spot" || permissionType === "both";
  const showOpenTradesTab =
    selectedMarket !== "spot" || canShowSpotOpenTrades;

  const tabCounts = useMemo(
    () => {
      const matchesMarket = (item) =>
        selectedMarket === "spot" ? item.isSpot : !item.isSpot;
      return {
        open: showOpenTradesTab
          ? openPosition.filter(matchesMarket).length
          : 0,
        closed: closePosition.filter(matchesMarket).length,
        pending: pendingOrders.filter(matchesMarket).length,
      };
    },
    [
      openPosition,
      closePosition,
      pendingOrders,
      selectedMarket,
      showOpenTradesTab,
    ]
  );

  const tabs = [
    ...(showOpenTradesTab
      ? [
          {
            key: "open",
            label: "Open Trades",
            shortLabel: "Open",
            count: tabCounts.open,
            icon: <OpenTradesIcon />,
          },
        ]
      : []),
    {
      key: "closed",
      label: "Closed Trades",
      shortLabel: "Closed",
      count: tabCounts.closed,
      icon: <ClosedTradesIcon />,
    },
    // Pending tab temporarily hidden — restore when ready.
    // {
    //   key: "pending",
    //   label: "Pending Orders",
    //   shortLabel: "Pending",
    //   count: tabCounts.pending,
    //   icon: <PendingOrdersIcon />,
    // },
  ];

  const currentTableData = useMemo(() => {
    if (activeTable === "open") return openPosition;
    if (activeTable === "pending") return pendingOrders;
    return closePosition;
  }, [activeTable, closePosition, openPosition, pendingOrders]);

  const filteredTableData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return currentTableData.filter((item) => {
      const spot = Boolean(item.isSpot);
      const matchesMarket = selectedMarket === "spot" ? spot : !spot;
      if (!matchesMarket) return false;
      if (!query) return true;
      return String(item.asset || "")
        .toLowerCase()
        .includes(query);
    });
  }, [currentTableData, searchQuery, selectedMarket]);

  const dateColumnLabel =
    activeTable === "open"
      ? "Opened"
      : activeTable === "pending"
      ? "Created"
      : "Closed";

  const totalPages = Math.max(
    1,
    Math.ceil(filteredTableData.length / itemsPerPage)
  );

  const paginatedData = useMemo(() => {
    return filteredTableData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filteredTableData, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTable, selectedMarket, searchQuery]);

  useEffect(() => {
    if (!showOpenTradesTab && activeTable === "open") {
      setActiveTable("closed");
    }
  }, [showOpenTradesTab, activeTable]);

  const callApi = async (market = selectedMarket) => {
    // Spot open trades require spot/both permission; closed history is separate.
    if (market === "spot" && !canShowSpotOpenTrades) {
      setOpenPosition([]);
      setPendingOrders([]);
      return;
    }

    try {
      const marketType = getMarketTypeParam(market);
      const response = await api(
        "post",
        `livetrade/getLiveTrade?marketType=${marketType}`
      );
      if (response?.data) {
        const data = response.data?.[0] || {};
        const openPositions = Array.isArray(data?.openPositions)
          ? data.openPositions
          : [];
        const pending = Array.isArray(data?.openOrders)
          ? data.openOrders
          : Array.isArray(data?.pendingOrders)
          ? data.pendingOrders
          : [];
        const forceSpot = market === "spot";

        setOpenPosition(
          openPositions.map((item) => {
            const timestamp = pickTimestamp(
              item.openTime,
              item.openedAt,
              item.updateTime,
              item.updatedTime,
              item.createdTime,
              item.createdAt
            );
            const spot = forceSpot || isSpotTrade(item);
            return {
              asset: formatAsset(item.symbol),
              assetRaw: item.symbol,
              type: spot ? "Spot" : "Futures",
              direction: formatTradeSide(item.side, item.positionAmt),
              dateLabel: formatDate(timestamp),
              pnl: `${Number(item.pnlPercentage || 0).toFixed(2)}%`,
              pnd: item.unRealizedProfit
                ? `$${Number(item.unRealizedProfit).toFixed(2)}`
                : "—",
              isVerifiedTrade: item.isVerifiedTrade,
              date: timestamp,
              blockchainLink: item?.blockchainLink?.blockChainUrl || "",
              isSpot: spot,
            };
          })
        );

        setPendingOrders(
          pending.map((item) => {
            const timestamp = pickTimestamp(
              item.time,
              item.openTime,
              item.updateTime,
              item.createdTime
            );
            const spot = forceSpot || isSpotTrade(item);
            return {
              asset: formatAsset(item.symbol),
              assetRaw: item.symbol,
              type: spot ? "Spot" : "Futures",
              direction: formatTradeSide(item.side, item.positionAmt),
              dateLabel: formatDate(timestamp),
              pnl: "—",
              pnd: "—",
              isVerifiedTrade: false,
              date: timestamp ?? undefined,
              blockchainLink: "",
              isSpot: spot,
            };
          })
        );
      }
    } catch (error) {
      setOpenPosition([]);
      setPendingOrders([]);
    }
  };

  const getTradeHistory = async () => {
    try {
      const response = await api("get", "users/getTradeHistory");
      if (response?.status) {
        const trades = Array.isArray(response?.data) ? response.data : [];
        setClosePosition(
          trades.map((item) => ({
            _id: item._id,
            asset: formatAsset(item.pair),
            assetRaw: item.pair,
            type: isSpotTrade(item) ? "Spot" : "Futures",
            direction: formatTradeSide(item.side, item.positionAmt),
            dateLabel: formatDate(item.timestamp),
            pnl: `${Number(
              item.pnlPercentage || item.roiPercentage || 0
            ).toFixed(2)}%`,
            pnd: `$${Number(item.realizedPnl || 0).toFixed(2)}`,
            isVerifiedTrade: item.isVerifiedTrade,
            blockchainLink: item.blockChainLink || "",
            isSpot: isSpotTrade(item),
          }))
        );
      }
    } catch (error) {
      setClosePosition([]);
    }
  };

  useEffect(() => {
    // Closed trades always loaded for CM/CL (any permissionType).
    getTradeHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  useEffect(() => {
    callApi(selectedMarket);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, selectedMarket, canShowSpotOpenTrades]);

  const renderAction = (row) => {
    if (row.isVerifiedTrade) {
      return (
        <button
          type="button"
          className="tradeListingVerifyBtn tradeListingVerifyBtn--verified"
          onClick={() => {
            if (row.blockchainLink) {
              window.open(row.blockchainLink, "_blank", "noopener,noreferrer");
            }
          }}
        >
          <img src={checkIcon} alt="" />
          Blockchain Verified
        </button>
      );
    }

    return (
      <button
        type="button"
        className="tradeListingVerifyBtn"
        onClick={() =>
          handleVerifyClick({
            row,
            dispatch,
            location,
            navigate,
            activeTable,
            user,
            selectedMarket,
          })
        }
      >
        Verify it
      </button>
    );
  };

  return (
    <div className="tradeListingTableWrapper" id="verifiedTradeHistory">
      <div className="tradeListingToolbar">
        <div className="tradeListingTabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTable === tab.key}
              aria-label={`${tab.label} (${formatCount(tab.count)})`}
              className={`tradeListingTabs__item${
                activeTable === tab.key ? " is-active" : ""
              }`}
              onClick={() => setActiveTable(tab.key)}
            >
              <span className="tradeListingTabs__icon">{tab.icon}</span>
              <span className="tradeListingTabs__label">
                <span className="tradeListingTabs__labelFull">
                  {tab.label} ({formatCount(tab.count)})
                </span>
                <span className="tradeListingTabs__labelShort">
                  {tab.shortLabel} ({formatCount(tab.count)})
                </span>
              </span>
            </button>
          ))}
        </div>

        <div className="tradeListingSearch text-end position-relative">
          <SearchBar
            value={searchQuery}
            searchValue="Search coin name"
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
      </div>

      <div className="tradeListingTableScroll">
        <table className="tradeListingTable">
          <thead>
            <tr>
              <th>Asset</th>
              <th>Type</th>
              <th>Trade</th>
              <th>{dateColumnLabel}</th>
              <th>PNL (%)</th>
              <th>PNL ($)</th>
              <th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr className="tradeListingTable__empty">
                <td colSpan={7}>
                  {searchQuery
                    ? `No results found for "${searchQuery}"`
                    : "No Data Found"}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => (
                <tr key={row._id || `${row.assetRaw}-${index}`}>
                  <td className="tradeListingTable__asset">{row.asset}</td>
                  <td>{row.type}</td>
                  <td>{row.direction}</td>
                  <td>{row.dateLabel}</td>
                  <td>{row.pnl}</td>
                  <td>{row.pnd}</td>
                  <td className="tradeListingTable__action">
                    {renderAction(row)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredTableData.length > 0 && (
        <div className="tradeListingPagination">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default TradeListingTable;
